/**
 * Hook: fetch the Nut Sheet Type lists for one location.
 *
 * Calls GET /config (the same endpoint as Configuration page) and picks the
 * `nutSheetTypes` slice. Returns empty arrays when the location has no
 * saved Type record yet — the page UI shows a guidance banner pointing to
 * Configuration → Nut Sheet Types so an Admin can seed.
 */

import { useEffect, useState } from 'react';
import {
  NutSheetTypesConfig,
  getWorkingDaysConfig,
} from '../services/api';

interface UseNutSheetTypesResult {
  newTypes: { code: string; label: string }[];
  usedTypes: { code: string; label: string }[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useNutSheetTypes(locationId: number): UseNutSheetTypesResult {
  const [allTypes, setAllTypes] = useState<NutSheetTypesConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getWorkingDaysConfig(refreshCounter > 0).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setAllTypes(res.data.nutSheetTypes ?? []);
      } else {
        setError(res.error || 'Failed to load Nut Sheet types');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshCounter]);

  const match = allTypes.find((t) => t.locationId === locationId);
  return {
    newTypes: match?.newTypes ?? [],
    usedTypes: match?.usedTypes ?? [],
    loading,
    error,
    refresh: () => setRefreshCounter((c) => c + 1),
  };
}
